import { formatMinutesAsShortDaysHoursMinutesSeconds } from '../../../../../../../shared/utils/date';
import { formatTextSegmentIntoJSX } from '../../../../../../../shared/utils/text-transformer';
import { ForwardMsgType } from '../../../../../../forward-msg-popup/state';
import { MessageOrigin, MessageTypes, MessageTypesEnum } from '../../../../typings/message-types';
import { getCallIConsAndStylesForChatPreview } from '../../../../utils/get-call-icons-and-styles-for-chat-preview';
import { maskPhone } from '../../../../utils/mask-phone';
import { calculateTime } from '../../../../utils/time';

export const getMaskedText = (phoneNumber: string | undefined, rawText: string) => {
  if (phoneNumber && rawText) {
    const maskedPhone = maskPhone(phoneNumber as string, 'X');
    const prevText = rawText.replaceAll(phoneNumber, maskedPhone);
    return prevText;
  }
  return rawText;
};

export const slaDueDateIsPast = (isSlaActive: boolean | undefined, slaDueDate: string | undefined) => {
  if (!isSlaActive || !slaDueDate) {
    return false;
  }
  return new Date(slaDueDate) < new Date();
};

export const getSlaText = (
  isSlaActive: boolean | undefined,
  slaDueDate: string | undefined,
  currentSlaTimer: number
) => {
  if (!isSlaActive || !slaDueDate) {
    return;
  }
  const dueInMins = Math.floor(Math.abs(new Date(slaDueDate).getTime() - currentSlaTimer) / 60000);
  if (slaDueDateIsPast(isSlaActive, slaDueDate)) {
    if (dueInMins < 1 && dueInMins >= 0) {
      return 'Response delayed';
    }
    return `Response delayed by ${formatMinutesAsShortDaysHoursMinutesSeconds(dueInMins, true)} `;
  }
  if (dueInMins <= 1) {
    return 'SLA is about to breach in a minute';
  }
  return `Response due in ${formatMinutesAsShortDaysHoursMinutesSeconds(dueInMins, true)} `;
};

export const getReactionPreview = ({
  message,
  messageOriginType,
  currentUserId,
}: {
  message: MessageTypes | undefined;
  messageOriginType: string | undefined;
  currentUserId: string | undefined;
}) => {
  if (!message) return null;

  const { reaction, agentReaction } = message;
  const hasCustomerReaction = reaction?.text;
  const hasAgentReaction = agentReaction?.text;

  let agentName = agentReaction?.agentName || agentReaction?.agentPhone;
  if (currentUserId === agentReaction?.agentId) {
    agentName = 'You';
  }

  const shortenedAgentName = agentName?.split(' ')[0] || 'Agent';

  // If no reactions, return null
  if (!hasCustomerReaction && !hasAgentReaction) return null;

  // If it's a customer's message
  if (messageOriginType === MessageOrigin.CUSTOMER) {
    // Don't show if customer reacted to their own message
    if (hasCustomerReaction && !hasAgentReaction) return null;

    // Show agent's reaction if present
    if (hasAgentReaction) {
      return {
        text: agentReaction.text,
        name: shortenedAgentName,
        isAgent: true,
      };
    }
  }

  // If it's an agent's message
  if (messageOriginType === MessageOrigin.USER) {
    // If both reacted, show customer's reaction (based on WhatsApp behavior)
    if (hasCustomerReaction && hasAgentReaction) {
      return {
        text: reaction.text,
        name: 'Customer',
        isAgent: false,
      };
    }

    // Show agent's reaction if only agent reacted
    if (hasAgentReaction) {
      return {
        text: agentReaction.text,
        name: shortenedAgentName,
        isAgent: true,
      };
    }

    // Show customer's reaction if only customer reacted
    if (hasCustomerReaction) {
      return {
        text: reaction.text,
        name: 'Customer',
        isAgent: false,
      };
    }
  }

  return null;
};

const preprocessDisplayText = (text: string) => {
  let trimText = text?.trim();
  trimText = trimText.replace(/(\r\n|\n|\r)/gm, ' ');
  const formattedText = formatTextSegmentIntoJSX(trimText);
  return formattedText;
};

export const getPreprocessDisplayText = ({ message }: { message: MessageTypes | undefined }) => {
  let text = '';
  switch (message?.messageType) {
    case MessageTypesEnum.TEMPLATE:
      text = message.templateMessage?.body.data[0].text || '';
      break;
    case MessageTypesEnum.TEXT:
      if (message.messageTypeWeb === MessageTypesEnum.FLOW_RESPONSE) {
        const payload = message?.userResponse?.payload;
        text = payload ? JSON.stringify(payload) : message.text || '';
        break;
      }
      text = message.text || '';
      break;
    case MessageTypesEnum.INTERACTIVE:
      text = message.interactiveMessage?.body?.text || 'Interactive message';
      break;
    case MessageTypesEnum.IMAGE:
      text = 'Image';
      break;
    case MessageTypesEnum.VIDEO:
      text = 'Video' + (message?.duration ? `${' '}(${calculateTime(message.duration) || '--:--'})` : '');
      break;
    case MessageTypesEnum.DOCUMENT:
      text = message?.fileName ?? 'Document';
      break;
    case MessageTypesEnum.AUDIO:
      text = 'Audio' + (message?.duration ? `${' '}(${calculateTime(message.duration) || '--:--'})` : '');
      break;
    case MessageTypesEnum.SYSTEM:
    case MessageTypesEnum.SYSTEM_LOG:
      text = message.text || '';
      break;
    case MessageTypesEnum.CONTACTS:
      text = 'Contacts';
      break;
    case MessageTypesEnum.BUTTON:
      text = message.text || '';
      break;
    case MessageTypesEnum.LOCATION:
      text = 'Location';
      break;
    case MessageTypesEnum.CALL:
      const { title } = getCallIConsAndStylesForChatPreview({
        callMessage: message,
        size: 16,
      });
      text =
        title + (message?.durationInSeconds ? `${' '}(${calculateTime(message.durationInSeconds) || '--:--'})` : '');
      break;
    default:
      text = '';
  }
  return preprocessDisplayText(text);
};

export const getBGcolor = ({
  forwardMsgView,
  forwardMsgState,
  openedChat,
  selectedChatIdsList,
  chatID,
  showUnreadUI,
  isAllChatsSelected,
  excludedChatIds,
}: {
  forwardMsgView: boolean | undefined;
  forwardMsgState: {
    showMsgsSelectView: false;
    showMsgsForwardPopup: false;
    forwardMsg: ForwardMsgType[];
  };
  openedChat: boolean;
  selectedChatIdsList: string[];
  chatID: string | undefined;
  showUnreadUI: boolean;
  isAllChatsSelected: boolean;
  excludedChatIds: string[];
}) => {
  const isInForwardPopup =
    forwardMsgView && Array.isArray(forwardMsgState.forwardMsg) && forwardMsgState.forwardMsg.length > 0;

  const isNotInForwardPopup =
    !forwardMsgView &&
    (!forwardMsgState?.forwardMsg ||
      (Array.isArray(forwardMsgState.forwardMsg) && forwardMsgState.forwardMsg.length === 0));

  if (isNotInForwardPopup && openedChat) {
    return 'rgb(234, 243, 240)';
  }

  const isChatSelected = isAllChatsSelected
    ? !excludedChatIds.includes(chatID ?? '')
    : selectedChatIdsList.includes(chatID ?? '');

  if (isChatSelected) {
    if (isInForwardPopup || isNotInForwardPopup) {
      return 'rgba(61, 144, 109, 0.1)';
    }
  }

  if (showUnreadUI || isInForwardPopup) {
    return '#FFFFFF';
  }

  return '#FFFFFF';
};

export const getNameColor = ({
  showUnreadUI,
  forwardMsgView,
}: {
  showUnreadUI: boolean;
  forwardMsgView: boolean | undefined;
}) => {
  if (showUnreadUI) {
    return 'rgba(40, 40, 40, 1)';
  }
  if (forwardMsgView) {
    return '#2d2d2d';
  }
  return 'rgba(122, 122, 122, 1)';
};
